import { Component, Inject, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin } from "rxjs";
import { RiskRegisterControlLinkType } from "src/app/enums/riskRegisterControlLinkType.enum";
import {
    ControlsSummaryPayload,
    RiskRegisterControl,
} from "src/app/models/risk-assessment.model";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { PdfExportService } from "src/app/services/pdf-export.service";
import { downloadBlob } from "src/app/shared/utils/download.util";
import { RiskRegisterService } from "src/app/services/risk-register.service";
import { ToastService } from "src/app/services/toast.service";
import { UserService } from "src/app/services/user.service";
import { ErrorConstants } from "src/app/shared/constants/error.constants";
import nameOf from "src/app/shared/form/nameOf.helper";
import { ControlStatusEnum } from "src/app/enums/control-status.enum";
import { TranslateService } from "src/app/services/translate.service";

@Component({
  standalone: false,
    selector: "app-risk-treatment-report",
    templateUrl: "./risk-treatment-report.component.html",
    styleUrls: ["./risk-treatment-report.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None,
})
export class RiskTreatmentReportComponent implements OnInit {
    currentDate: string;
    logoUrl: string;
    isExporting = false;
    dataSource = [];
    noDataError = ErrorConstants.errorHtml;
    displayedColumns: string[] = [];
    controlStatusEnum = ControlStatusEnum;

    constructor(
        public dialogRef: MatDialogRef<RiskTreatmentReportComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { assessmentIds: string[] },
        private userService: UserService,
        private pdfService: PdfExportService,
        private riskRegisterService: RiskRegisterService,
        private toastService: ToastService,
        private dateFormatService: DateFormatService,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        this.displayedColumns = Object.keys(this.getRiskColumns());
        this.currentDate = this.dateFormatService.formatDateTimeToString(
            new Date(),
            this.dateFormatService.userDateFormat,
        );
        const payload: ControlsSummaryPayload = {
            riskAssessmentIds: this.data.assessmentIds,
            riskRegisterControlLinkType:
                RiskRegisterControlLinkType.RiskTreatment,
        };
        forkJoin({
            controlsSummary:
                this.riskRegisterService.getControlsSummary(payload),
            logoUrl: this.userService.getTenantLogoUrl(),
        }).subscribe({
            next: (res) => {
                this.logoUrl = res.logoUrl;
                this.dataSource = res.controlsSummary;
            },
            error: (error) => {
                this.toastService.showError(error);
            },
        });
    }

    save(orientation: "landscape" | "portrait" = "landscape") {
        this.isExporting = true;
        const dateString = (date) =>
            this.dateFormatService.formatTimestampToString(
                date,
                this.dateFormatService.userDateFormat,
            );
        setTimeout(async () => {
            const doc = await this.pdfService.createDocument(orientation);

            if (this.logoUrl) {
                this.pdfService.addContent(doc, this.logoUrl, {
                    type: "image",
                    width: 60,
                    height: 30,
                    align: "left",
                    spacing: 20,
                    sameLine: true,
                });
            }

            this.pdfService.addContent(
                doc,
                `${this.translateService.getDynamicTranslation("Report Date")}: ${dateString(this.currentDate)}`,
                {
                    align: "right",
                    fontSize: 10,
                    spacing: this.logoUrl ? 0 : 20,
                    sameLine: this.logoUrl ? true : false,
                },
            );

            this.pdfService.addTitle(
                doc,
                this.translateService.getDynamicTranslation(
                    "Risk Treatment Report",
                ),
            );

            const headerRow = this.displayedColumns.map(
                (col) => this.getRiskColumns()[col] || col,
            );
            const bodyRows = this.dataSource.map((risk) => {
                return this.displayedColumns.map((col) => {
                    if (
                        col === nameOf<RiskRegisterControl>((x) => x.status) &&
                        risk[col] != null
                    )
                        return ControlStatusEnum[risk[col]];
                    return risk[col] ?? "-";
                });
            });
            await this.pdfService.addTable(doc, headerRow, bodyRows);

            this.pdfService.addContent(
                doc,
                `${this.translateService.getDynamicTranslation("Included Risk Assessment IDs")}: ${this.data.assessmentIds.join(", ")}`,
                {
                    bold: false,
                    spacing: 20,
                },
            );

            this.pdfService.saveDocument(doc, "risk-treatment-report.pdf");

            this.isExporting = false;
        }, 100);
    }

    async exportToExcel() {
        const [{ Workbook }] = await Promise.all([
            import(/* webpackChunkName: "exceljs" */ "exceljs"),
        ]);
        const wb = new Workbook();
        const ws = wb.addWorksheet(
            this.translateService.getDynamicTranslation("Risk Treatment"),
        );

        const headerMap = this.getRiskColumns();
        const headers = this.displayedColumns.map((c) => ({
            header: headerMap[c] || c,
            key: c,
        }));
        ws.columns = headers;

        ws.columns.forEach((col) => {
            col.width = 20;
        });

        this.dataSource.forEach((row) => {
            ws.addRow(
                this.displayedColumns.map((col) => {
                    if (
                        col === nameOf<RiskRegisterControl>((x) => x.status) &&
                        row[col] != null
                    )
                        return ControlStatusEnum[row[col]];
                    return row[col] ?? "-";
                }),
            );
        });

        ws.getRow(1).font = { bold: true };
        ws.getRow(1).alignment = {
            vertical: "middle",
            horizontal: "center",
            wrapText: true,
        };

        ws.eachRow((row, rowNumber) => {
            if (rowNumber > 1) {
                row.alignment = {
                    vertical: "top",
                    horizontal: "center",
                    wrapText: true,
                };
            }
        });

        wb.xlsx
            .writeBuffer()
            .then((buf) =>
                downloadBlob(new Blob([buf]), "risk-treatment-report.xlsx"),
            );
    }

    getRiskColumns() {
        return {
            [nameOf<RiskRegisterControl>((x) => x.controlName)]:
                this.translateService.getDynamicTranslation("Control Name"),
            [nameOf<RiskRegisterControl>((x) => x.controlBucketName)]:
                this.translateService.getDynamicTranslation(
                    "Control Bucket Name",
                ),
            [nameOf<RiskRegisterControl>((x) => x.status)]:
                this.translateService.getDynamicTranslation("Status"),
            [nameOf<RiskRegisterControl>((x) => x.controlOwnerName)]:
                this.translateService.getDynamicTranslation("Control Owner"),
            [nameOf<RiskRegisterControl>((x) => x.implementationStartDate)]:
                this.translateService.getDynamicTranslation(
                    "Implementation Start Date",
                ),
            [nameOf<RiskRegisterControl>((x) => x.implementationEndDate)]:
                this.translateService.getDynamicTranslation(
                    "Implementation End Date",
                ),
            [nameOf<RiskRegisterControl>((x) => x.targetImplementationStart)]:
                this.translateService.getDynamicTranslation(
                    "Target Implementation Start Date",
                ),
            [nameOf<RiskRegisterControl>((x) => x.targetImplementationEnd)]:
                this.translateService.getDynamicTranslation(
                    "Target Implementation End Date",
                ),
        };
    }

    cancel() {
        this.dialogRef.close();
    }
}
